import { useState, useMemo } from "react";
import { formatINR } from "@/lib/units";
import ResultsPanel, { CalcPageHeader, Field } from "@/components/ResultsPanel";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

export default function LoanEMI() {
  const [principal, setPrincipal] = useState("5000000");
  const [rate, setRate] = useState("8.5");
  const [years, setYears] = useState("20");

  const data = useMemo(() => {
    const P = Number(principal) || 0;
    const r = ((Number(rate) || 0) / 12) / 100;
    const n = (Number(years) || 0) * 12;
    if (!P || !r || !n) return { emi: 0, interest: 0, total: 0, schedule: [] };
    const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const total = emi * n;
    const interest = total - P;

    // Yearly amortization
    const schedule = [];
    let balance = P;
    for (let y = 1; y <= Number(years); y++) {
      let yearInterest = 0, yearPrincipal = 0;
      for (let m = 0; m < 12 && balance > 0; m++) {
        const i = balance * r;
        const p = emi - i;
        balance -= p;
        yearInterest += i;
        yearPrincipal += p;
      }
      schedule.push({
        year: `Y${y}`,
        principal: Math.round(yearPrincipal),
        interest: Math.round(yearInterest),
        balance: Math.max(0, Math.round(balance)),
      });
    }
    return { emi, interest, total, schedule };
  }, [principal, rate, years]);

  const rows = [
    ["Loan Amount", formatINR(Number(principal)||0)],
    ["Interest Rate", `${rate}% p.a.`],
    ["Tenure", `${years} years`],
    ["Monthly EMI", formatINR(data.emi)],
    ["Total Interest", formatINR(data.interest)],
    ["Total Payment", formatINR(data.total)],
  ];

  return (
    <div>
      <CalcPageHeader title="Loan EMI Calculator" description="EMI, total interest and yearly amortization for home / property loans." slug="loan-emi" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-7 space-y-6">
          <div className="rounded-lg border border-border bg-card p-5 sm:p-6 space-y-4">
            <Field label="Loan Amount (₹)"><input data-testid="emi-principal" type="number" value={principal} onChange={(e)=>setPrincipal(e.target.value)} className="w-full h-11 px-3 rounded-md border border-border bg-background font-mono outline-none focus:ring-2 focus:ring-primary/40" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Interest Rate % p.a."><input data-testid="emi-rate" type="number" step="0.05" value={rate} onChange={(e)=>setRate(e.target.value)} className="w-full h-11 px-3 rounded-md border border-border bg-background font-mono outline-none focus:ring-2 focus:ring-primary/40" /></Field>
              <Field label="Tenure (years)"><input data-testid="emi-years" type="number" value={years} onChange={(e)=>setYears(e.target.value)} className="w-full h-11 px-3 rounded-md border border-border bg-background font-mono outline-none focus:ring-2 focus:ring-primary/40" /></Field>
            </div>
          </div>
          <div className="rounded-lg border border-border bg-card p-5 sm:p-6">
            <div className="overline mb-3">Yearly amortization</div>
            <div className="h-72" data-testid="emi-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.schedule}>
                  <defs>
                    <linearGradient id="gp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4}/>
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                  <XAxis dataKey="year" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v)=>`₹${(v/100000).toFixed(0)}L`} />
                  <Tooltip formatter={(v)=>formatINR(v)} contentStyle={{background:"hsl(var(--card))",border:"1px solid hsl(var(--border))",borderRadius:6}} />
                  <Legend wrapperStyle={{fontSize:12}} />
                  <Area type="monotone" dataKey="principal" stackId="1" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#gp)" />
                  <Area type="monotone" dataKey="interest" stackId="1" stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} fill="hsl(var(--muted-foreground) / 0.15)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <ResultsPanel title="Loan EMI" slug="loan-emi" highlight={{ label: "Monthly EMI", value: formatINR(data.emi), sub: `Interest: ${formatINR(data.interest)}` }} rows={rows} onReset={()=>{setPrincipal("");}} />
        </div>
      </div>
    </div>
  );
}
